import { Platform } from 'react-native';
import { httpsCallable } from 'firebase/functions';
import { functions as webFunctions } from './firebaseConfig';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * BORRAR LA CUENTA — la llamada, en un solo sitio
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Esto vivía dentro de `app/(tabs)/profile.tsx`. Se saca aquí porque hacía
 * falta desde más de una pantalla, y duplicar una llamada que BORRA DATOS es
 * pedir que dentro de seis meses una de las dos copias tenga el tiempo de
 * espera viejo o la palabra de confirmación cambiada.
 *
 * ── POR QUÉ HIZO FALTA ───────────────────────────────────────────────────
 *
 * La guideline 5.1.1(v) de la App Store exige que el borrado se pueda INICIAR
 * Y COMPLETAR desde dentro de la app, para TODA cuenta que la app permita
 * crear. Un correo o un WhatsApp son un extra, no un sustituto.
 *
 * Y había dos sitios donde no se podía:
 *
 *   · EL DUEÑO DE RESTAURANTE. El botón de borrar vive solo en
 *     `(tabs)/profile.tsx`, pero `_layout.tsx` manda ese rol a
 *     `/restaurant-dashboard`, que es una pantalla del stack raíz, sin
 *     cabecera, sin «atrás» y sin un solo enlace al perfil. O sea: una cuenta
 *     real —con el número COMPLETO de cédula del dueño guardado, que según
 *     `privacy.tsx` es la única excepción de toda la app— sin ninguna vía de
 *     borrado. Y encima `privacy.tsx` y `terms.tsx` le dicen por escrito que
 *     vaya a «Perfil → Eliminar Mi Cuenta»: una instrucción imposible.
 *
 *   · QUIEN SE REGISTRA Y NO RECIBE EL CORREO. `auth.tsx` crea el usuario y
 *     escribe /users/{uid} con correo, teléfono y rol ANTES de verificar nada,
 *     y luego deja a la persona en `/verify-email`, donde las únicas salidas
 *     eran «Ya verifiqué», «Reenviar» y «Cerrar sesión» — y cerrar sesión no
 *     borra nada. Quien se equivocó al escribir su correo, o a quien el correo
 *     le cayó en spam, se quedaba con una cuenta viva y sus datos dentro, sin
 *     forma de deshacerla. Apple prueba exactamente ese estado intermedio.
 */

/** La palabra exacta que el usuario escribe. La misma que exige el servidor. */
export const FRASE_BORRADO = 'ELIMINAR';

/**
 * Tiempo de espera del cliente, EN MILISEGUNDOS.
 *
 * `httpsCallable` se rinde solo a los ~70 s (web) / ~60 s (nativo). Pero el
 * borrado real recorre viajes, pedidos, asientos contables y seis carpetas de
 * Storage: en una cuenta de chofer con historial tarda varios minutos, y por
 * eso la Cloud Function está declarada con `timeoutSeconds: 540`
 * (functions/src/cuenta.ts).
 *
 * Lo que pasaba con el valor por defecto: al minuto el cliente cortaba y la
 * pantalla decía «No pudimos eliminar tu cuenta» MIENTRAS EL SERVIDOR SEGUÍA
 * BORRANDO. La persona creía que había fallado y volvía a intentarlo, cuando
 * su cuenta ya estaba desapareciendo.
 *
 * 550 s le dejan al servidor agotar sus 540 y devolver SU propio error, con su
 * motivo, en vez de que lo tape un corte nuestro.
 *
 * UNIDADES — las dos ramas reciben MILISEGUNDOS y es correcto en ambas:
 *   · SDK web (`firebase/functions`): `timeout` está documentado en ms.
 *   · `@react-native-firebase/functions`: también recibe ms y los divide entre
 *     1000 antes de pasarlos al SDK nativo, que trabaja en segundos. Por eso
 *     el valor es múltiplo exacto de 1000: 550000/1000 = 550, un entero limpio
 *     para el `getInt("timeout")` del lado Android.
 */
export const ESPERA_BORRADO_MS = 550_000;

export async function llamarBorradoDeCuenta(
  datos: Record<string, unknown>
): Promise<any> {
  if (Platform.OS !== 'web') {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { getApp } = require('@react-native-firebase/app');
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { getFunctions, httpsCallable: nativeHttpsCallable } = require('@react-native-firebase/functions');
    const fn = nativeHttpsCallable(getFunctions(getApp()), 'solicitarBorradoDeCuenta', {
      timeout: ESPERA_BORRADO_MS,
    });
    const res = await fn(datos);
    return res?.data;
  }
  const fn = httpsCallable(webFunctions, 'solicitarBorradoDeCuenta', {
    timeout: ESPERA_BORRADO_MS,
  });
  const res = await fn(datos);
  return res?.data;
}
